import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo } from "react";
import { detectShippingCarrierFromUrl } from "@shared/types/shipping";
import { getDomain } from "@shared/blocks/helpers";
import { track } from "@/lib/analytics";
import { linkTypeFromHref } from "@/lib/entry-context";
import boxImg from "@/assets/images/box.png";
import topTapeImg from "@/assets/images/top-tape.png";
import leftTapeImg from "@/assets/images/left-tape.png";
import bottomTapeImg from "@/assets/images/bottom-tape.png";
import "./shipping-link-block-content.scss";
// TODO(shipping-carrier-assets): Ship dedicated carrier SVGs (or sized marks) and
// render those instead of s2 favicons so UPS/FedEx (and future carriers) have
// visually balanced, proportionate heights without per-carrier width/height hacks.
const CARRIER_FAVICON_PX = 16;
function getFaviconUrl(domain) {
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`;
}
function titleLabel(status) {
    if (status === "out_for_delivery")
        return "Arriving Today";
    if (status === "delivered")
        return "Delivered";
    if (status === "in_transit")
        return "In Transit";
    if (status === "label_created")
        return "Shipment Created";
    if (status === "exception")
        return "Delivery Exception";
    // TODO(shipping-copy): Replace placeholder date copy with canonical arrival
    // date field from ingestor shipping metadata once wired.
    return "Arriving Today";
}
function resolveShippingDisplayDataFromMetadata(content) {
    if (content.subtype !== "shipping")
        return undefined;
    const shipping = content.shipping;
    if (!shipping.carrier)
        return undefined;
    return {
        carrier: shipping.carrier,
        trackingNumber: shipping.trackingNumber || "TRACKING",
        title: titleLabel(shipping.status),
        eta: shipping.estimatedDeliveryText ?? "Estimated between 11:20 AM - 3:20 PM",
    };
}
function generateBarcodePattern(trackingNumber, carrier) {
    // TODO(shipping-barcode): Replace pseudo pattern generation with carrier-accurate
    // barcode rules once validated format specs are finalized.
    const seed = `${carrier ?? "generic"}:${trackingNumber}`;
    const bars = [];
    let state = 7;
    for (let i = 0; i < seed.length; i++) {
        state = ((state * 33) ^ seed.charCodeAt(i)) % 9973;
    }
    // Keep a scannable-looking mix of thin/medium bars with visible rhythm.
    const segments = 28;
    const maxWidth = 3;
    for (let i = 0; i < segments; i++) {
        state = (state * 1103515245 + 12345) & 0x7fffffff;
        bars.push((state % maxWidth) + 1);
    }
    return bars;
}
function inferCarrierFromUrl(url) {
    return detectShippingCarrierFromUrl(url);
}
function extractTrackingNumber(url) {
    if (!url)
        return undefined;
    const match = url.match(/\b(1Z[0-9A-Z]{16}|\d{9,22})\b/i);
    return match?.[1]?.toUpperCase();
}
function resolveShippingDisplayDataFallbackFromUrl(content) {
    const carrier = inferCarrierFromUrl(content.url);
    if (!carrier)
        return undefined;
    return {
        carrier,
        trackingNumber: extractTrackingNumber(content.url) ?? "TRACKING",
        title: titleLabel(undefined),
        eta: "Estimated between 11:20 AM - 3:20 PM",
    };
}
function resolveShippingDisplayData(content) {
    return resolveShippingDisplayDataFromMetadata(content) ?? resolveShippingDisplayDataFallbackFromUrl(content);
}
function ShippingBarcode({ trackingNumber, carrier }) {
    const bars = generateBarcodePattern(trackingNumber, carrier);
    const gap = 1;
    const width = bars.reduce((sum, barWidth) => sum + barWidth + gap, 0);
    let x = 0;
    return (_jsx("svg", { className: "shipping-link-content__barcode-svg", viewBox: `0 0 ${width} 38`, preserveAspectRatio: "none", "aria-hidden": true, children: bars.map((barWidth, index) => {
            const barX = x;
            x += barWidth + gap;
            return _jsx("rect", { x: barX, y: 0, width: barWidth, height: 38, fill: "#121212" }, `${index}-${barWidth}`);
        }) }));
}
export const ShippingLinkBlockContent = memo(({ block }) => {
    const content = block.content;
    const displayData = resolveShippingDisplayData(content);
    const domain = getDomain(content.url) ?? content.domain;
    const faviconUrl = domain ? getFaviconUrl(domain) : undefined;
    // TODO(shipping-staging-fallback): Remove URL-only fallback once staging ingestor
    // always populates content.shipping for UPS/FedEx captures.
    // TODO(shipping-refresh): Show metadata-driven copy only; once refresh-on-read
    // exists, this component should not infer live state from URL shape.
    if (!displayData)
        return null;
    return (_jsxs("div", { className: "shipping-link-content", children: [_jsx("img", { className: "shipping-link-content__box", src: boxImg, alt: "" }), _jsx("img", { className: "shipping-link-content__tape shipping-link-content__tape--top", src: topTapeImg, alt: "" }), _jsx("img", { className: "shipping-link-content__tape shipping-link-content__tape--left", src: leftTapeImg, alt: "" }), _jsx("img", { className: "shipping-link-content__tape shipping-link-content__tape--bottom", src: bottomTapeImg, alt: "" }), _jsxs("div", { className: "shipping-link-content__foreground", children: [_jsxs("div", { className: "shipping-link-content__label", children: [_jsx("a", { className: "shipping-link-content__carrier", href: content.url, target: "_blank", rel: "noopener noreferrer", onClick: (event) => {
                                    event.stopPropagation();
                                    if (content.url) {
                                        track({
                                            name: "block.external_link_opened",
                                            props: { link_type: linkTypeFromHref(content.url), block_type: block.type },
                                        });
                                    }
                                }, onMouseDown: (event) => event.stopPropagation(), "aria-label": displayData.title, children: faviconUrl ? _jsx("img", { src: faviconUrl, alt: "", width: CARRIER_FAVICON_PX, height: CARRIER_FAVICON_PX }) : null }), _jsx("div", { className: "shipping-link-content__title", children: displayData.title }), _jsx("div", { className: "shipping-link-content__eta", children: displayData.eta })] }), _jsx("div", { className: "shipping-link-content__barcode-slot", children: _jsxs("div", { className: "shipping-link-content__barcode-wrap", children: [_jsx(ShippingBarcode, { trackingNumber: displayData.trackingNumber, carrier: displayData.carrier }), _jsx("div", { className: "shipping-link-content__tracking-number", children: displayData.trackingNumber })] }) })] })] }));
});
